import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FaPlay, FaPause, FaCheck, FaTimes, 
  FaDumbbell, FaInfoCircle, FaChevronDown, FaChevronUp,
  FaRedo, FaFlagCheckered, FaYoutube, FaClock, FaStopwatch,
  FaStickyNote, FaPlus, FaMinus, FaExchangeAlt, FaTrash
} from 'react-icons/fa';
import {
  getActiveWorkoutDraft,
  getActiveWorkoutDraftKey,
  getStoredWorkoutUserId,
  removeActiveWorkoutDraft,
  upsertActiveWorkoutDraft
} from '../../utils/activeWorkoutDrafts';
import ExerciseSearchPicker from './ExerciseSearchPicker';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { displayToGymKg, gymKgToDisplay } from '../../utils/weightUnits';
import './ActiveWorkout.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const ActiveWorkout = () => {
  const weightUnit = useWeightUnit();
  const { workoutId } = useParams();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('scheduleId');
  const navigate = useNavigate();
  const userId = getStoredWorkoutUserId();
  const draftKey = getActiveWorkoutDraftKey({ userId, workoutId, scheduleId });
  
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allExercises, setAllExercises] = useState([]);
  const [exerciseLogs, setExerciseLogs] = useState({});
  const [workoutStartTime, setWorkoutStartTime] = useState(new Date());
  const [expandedInfo, setExpandedInfo] = useState({}); // per-exercise info toggle
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const exitingClearRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [videoExercise, setVideoExercise] = useState(null);
  const [lastWeights, setLastWeights] = useState({}); // exerciseId -> {weight, reps}
  const [workoutNotes, setWorkoutNotes] = useState('');
  // Add/swap exercise picker: null | { mode: 'add' } | { mode: 'swap', exIdx }
  const [pickerTarget, setPickerTarget] = useState(null);
  
  // Rest timer state (floating, applies to any set)
  const [restTimer, setRestTimer] = useState(null); // { seconds, exerciseIdx, setIdx }
  const [restTimeRemaining, setRestTimeRemaining] = useState(null);
  const [isRestRunning, setIsRestRunning] = useState(false);
  const restRef = useRef(null);
  
  const exerciseRefs = useRef({});

  const scrollToExercise = useCallback((exIdx) => {
    const node = exerciseRefs.current[exIdx];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const mergeDraftLogs = useCallback((baseLogs, savedLogs) => {
    if (!savedLogs) return baseLogs;
    const merged = { ...baseLogs };
    Object.entries(baseLogs).forEach(([index, log]) => {
      const saved = savedLogs[index];
      if (!saved) return;
      // Merge base sets with saved data
      const mergedSets = log.sets.map((set, setIndex) => ({
        ...set,
        ...(saved.sets?.[setIndex] || {})
      }));
      // Restore any extra sets added during the draft session
      if (saved.sets && saved.sets.length > log.sets.length) {
        for (let i = log.sets.length; i < saved.sets.length; i++) {
          mergedSets.push(saved.sets[i]);
        }
      }
      merged[index] = {
        ...log,
        ...saved,
        sets: mergedSets
      };
    });
    return merged;
  }, []);

  useEffect(() => {
    exitingClearRef.current = false;
    fetchWorkout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (restRef.current) clearInterval(restRef.current);
    };
  }, []);

  const fetchLastWeights = useCallback(async (exerciseIds) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || exerciseIds.length === 0) return {};
      const res = await fetch(
        `${API_URL}/fitness/last-weights?exerciseIds=${exerciseIds.join(',')}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setLastWeights(data);
        return data;
      }
    } catch (err) {
      console.error('Error fetching last weights:', err);
    }
    return {};
  }, []);

  const fetchWorkout = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/fitness/workouts/${workoutId}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (!response.ok) throw new Error('Failed to fetch workout');
      const data = await response.json();
      setWorkout(data);
      
      const exercises = [];
      const processBlock = (block, blockName, blockType) => {
        if (!block?.exercises) return;
        block.exercises.forEach((ex) => {
          exercises.push({
            ...ex,
            blockName,
            blockType,
            blockRounds: block.rounds || 1,
            blockRestBetweenRounds: block.restBetweenRounds || 60
          });
        });
      };
      
      if (data.warmup) processBlock(data.warmup, data.warmup.name, 'warmup');
      data.blocks?.forEach(block => processBlock(block, block.name, 'main'));
      if (data.cooldown) processBlock(data.cooldown, data.cooldown.name, 'cooldown');
      
      const savedDraft = getActiveWorkoutDraft({ userId, workoutId, scheduleId });
      // If the user added/swapped exercises in a prior session, the saved list
      // is the source of truth (it already embeds the exercise metadata and its
      // indices line up with the saved logs). Otherwise use the template.
      const resolvedExercises =
        savedDraft?.exerciseList && savedDraft.exerciseList.length > 0
          ? savedDraft.exerciseList
          : exercises;

      // Fetch last weights, then init logs with pre-filled weights
      const exerciseIds = [...new Set(resolvedExercises.map(e => e.exerciseId).filter(Boolean))];
      const weights = await fetchLastWeights(exerciseIds);
      
      // Initialize exercise logs with pre-filled weights
      const logs = {};
      resolvedExercises.forEach((ex, idx) => {
        const sets = ex.sets || 1;
        const lastWeight = weights[ex.exerciseId];
        logs[idx] = {
          exerciseId: ex.exerciseId,
          exerciseName: ex.exerciseDetails?.name || 'Unknown',
          sets: Array(sets).fill(null).map((_, i) => ({
            setNumber: i + 1,
            reps: lastWeight?.reps || ex.reps || ex.exerciseDetails?.defaultReps || null,
            weight: lastWeight?.weight || null,
            duration: ex.duration || ex.exerciseDetails?.defaultDuration || null,
            completed: false
          })),
          completed: false,
          skipped: false
        };
      });
      const hydratedLogs = mergeDraftLogs(logs, savedDraft?.exerciseLogs);

      setAllExercises(resolvedExercises);
      setExerciseLogs(hydratedLogs);
      setWorkoutNotes(savedDraft?.workoutNotes || '');
      setExpandedInfo(savedDraft?.expandedInfo || {});
      if (savedDraft?.startedAt) {
        setWorkoutStartTime(new Date(savedDraft.startedAt));
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Rest timer ---
  const startRestTimer = (seconds, exerciseIdx, setIdx) => {
    if (restRef.current) clearInterval(restRef.current);
    setRestTimer({ seconds, exerciseIdx, setIdx });
    setRestTimeRemaining(seconds);
    setIsRestRunning(true);
    
    restRef.current = setInterval(() => {
      setRestTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(restRef.current);
          restRef.current = null;
          setIsRestRunning(false);
          if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const dismissRestTimer = () => {
    if (restRef.current) clearInterval(restRef.current);
    restRef.current = null;
    setRestTimer(null);
    setRestTimeRemaining(null);
    setIsRestRunning(false);
  };

  const pauseRestTimer = () => {
    if (restRef.current) { clearInterval(restRef.current); restRef.current = null; }
    setIsRestRunning(false);
  };

  const resumeRestTimer = () => {
    if (restTimeRemaining > 0) {
      setIsRestRunning(true);
      restRef.current = setInterval(() => {
        setRestTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(restRef.current);
            restRef.current = null;
            setIsRestRunning(false);
            if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // --- Set updates ---
  const updateSetField = (exIdx, setIdx, field, value) => {
    setExerciseLogs(prev => {
      const updated = { ...prev };
      updated[exIdx] = { ...updated[exIdx], sets: [...updated[exIdx].sets] };
      updated[exIdx].sets[setIdx] = { ...updated[exIdx].sets[setIdx], [field]: value };
      // Auto-fill weight to subsequent sets that haven't been manually changed
      if (field === 'weight' && value !== null) {
        for (let i = setIdx + 1; i < updated[exIdx].sets.length; i++) {
          if (!updated[exIdx].sets[i].completed) {
            updated[exIdx].sets[i] = { ...updated[exIdx].sets[i], weight: value };
          }
        }
      }
      return updated;
    });
  };

  const toggleSetComplete = (exIdx, setIdx) => {
    const isNowComplete = !exerciseLogs[exIdx]?.sets[setIdx]?.completed;
    setExerciseLogs(prev => {
      const updated = { ...prev };
      updated[exIdx] = { ...updated[exIdx], sets: [...updated[exIdx].sets] };
      updated[exIdx].sets[setIdx] = { ...updated[exIdx].sets[setIdx], completed: isNowComplete };
      // Mark exercise completed if all sets are done
      updated[exIdx].completed = updated[exIdx].sets.every(s => s.completed);
      return updated;
    });
    
    // Start rest timer after completing a set (if not the last set)
    if (isNowComplete) {
      const totalSets = exerciseLogs[exIdx]?.sets.length || 0;
      const completedCount = exerciseLogs[exIdx]?.sets.filter(s => s.completed).length || 0;
      if (completedCount < totalSets) {
        const restTime = allExercises[exIdx]?.restAfter || allExercises[exIdx]?.exerciseDetails?.restBetweenSets || 60;
        if (restTime > 0) startRestTimer(restTime, exIdx, setIdx);
      }
    }
  };

  const addSet = (exIdx) => {
    setExerciseLogs(prev => {
      const updated = { ...prev };
      const log = updated[exIdx];
      const lastSet = log.sets[log.sets.length - 1];
      updated[exIdx] = {
        ...log,
        sets: [
          ...log.sets,
          {
            setNumber: log.sets.length + 1,
            reps: lastSet?.reps || null,
            weight: lastSet?.weight || null,
            duration: lastSet?.duration || null,
            completed: false
          }
        ]
      };
      return updated;
    });
  };

  const removeSet = (exIdx) => {
    const originalCount = allExercises[exIdx]?.sets || 1;
    setExerciseLogs(prev => {
      const log = prev[exIdx];
      if (!log || log.sets.length <= originalCount) return prev;
      const updated = { ...prev };
      updated[exIdx] = {
        ...log,
        sets: log.sets.slice(0, -1)
      };
      return updated;
    });
  };

  const toggleSkipExercise = (exIdx) => {
    setExerciseLogs(prev => {
      const updated = { ...prev };
      updated[exIdx] = { ...updated[exIdx], skipped: !updated[exIdx].skipped };
      return updated;
    });
  };

  // --- Add / swap exercise (mid-workout) ---
  // Shape a raw catalog exercise into the flattened entry used by allExercises.
  const buildExerciseFromCatalog = (raw, blockType = 'main') => ({
    exerciseId: raw.exerciseId,
    exerciseDetails: raw,
    sets: raw.defaultSets || 3,
    reps: raw.defaultReps || null,
    duration: raw.defaultDuration || null,
    restAfter: raw.restBetweenSets || 45,
    blockName: 'Added',
    blockType,
    notes: '',
    _added: true
  });

  const buildFreshLog = (exercise) => {
    const numSets = exercise.sets || 1;
    return {
      exerciseId: exercise.exerciseId,
      exerciseName: exercise.exerciseDetails?.name || 'Unknown',
      sets: Array(numSets).fill(null).map((_, i) => ({
        setNumber: i + 1,
        reps: exercise.reps || exercise.exerciseDetails?.defaultReps || null,
        weight: null,
        duration: exercise.duration || exercise.exerciseDetails?.defaultDuration || null,
        completed: false
      })),
      completed: false,
      skipped: false
    };
  };

  const mergeLastWeightsFor = useCallback(async (exerciseId) => {
    if (!exerciseId) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(
        `${API_URL}/fitness/last-weights?exerciseIds=${encodeURIComponent(exerciseId)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setLastWeights(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Error fetching last weights for added exercise:', err);
    }
  }, []);

  const handlePickExercise = (raw) => {
    if (!raw) return;
    if (pickerTarget?.mode === 'swap' && Number.isInteger(pickerTarget.exIdx)) {
      const exIdx = pickerTarget.exIdx;
      const blockType = allExercises[exIdx]?.blockType || 'main';
      const newExercise = {
        ...buildExerciseFromCatalog(raw, blockType),
        blockName: allExercises[exIdx]?.blockName || 'Added'
      };
      setAllExercises(prev => {
        const updated = [...prev];
        updated[exIdx] = newExercise;
        return updated;
      });
      setExerciseLogs(prev => ({ ...prev, [exIdx]: buildFreshLog(newExercise) }));
    } else {
      // Add: append to the end of the list
      const newExercise = buildExerciseFromCatalog(raw);
      const newIndex = allExercises.length;
      setAllExercises(prev => [...prev, newExercise]);
      setExerciseLogs(prev => ({ ...prev, [newIndex]: buildFreshLog(newExercise) }));
      setTimeout(() => scrollToExercise(newIndex), 100);
    }
    mergeLastWeightsFor(raw.exerciseId);
    setPickerTarget(null);
  };

  // Remove an exercise mid-workout. Logs and per-exercise UI state are keyed by
  // array index, so shift every key above the removed index down by one to keep
  // them aligned with allExercises.
  const handleRemoveExercise = (exIdx) => {
    const name = allExercises[exIdx]?.exerciseDetails?.name || 'this exercise';
    if (!window.confirm(`Remove ${name} from your workout?`)) return;

    const reindex = (obj) => {
      const next = {};
      Object.keys(obj)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((key) => {
          if (key === exIdx) return;
          next[key > exIdx ? key - 1 : key] = obj[key];
        });
      return next;
    };

    setAllExercises(prev => prev.filter((_, i) => i !== exIdx));
    setExerciseLogs(prev => reindex(prev));
    setExpandedInfo(prev => reindex(prev));
  };

  useEffect(() => {
    if (exitingClearRef.current) return;
    if (loading || !workout || Object.keys(exerciseLogs).length === 0) return;

    const completedExercises = Object.values(exerciseLogs).filter(
      log => log.completed || log.skipped || log.sets?.some(set => set.completed)
    ).length;

    upsertActiveWorkoutDraft({
      draftKey,
      userId,
      workoutId,
      scheduleId,
      workoutName: workout.name,
      startedAt: workoutStartTime.toISOString(),
      updatedAt: new Date().toISOString(),
      exerciseLogs,
      exerciseList: allExercises,
      expandedInfo,
      workoutNotes,
      totalExercises: allExercises.length,
      completedExercises,
      progress: allExercises.length > 0 ? Math.round((completedExercises / allExercises.length) * 100) : 0
    });
  }, [
    allExercises,
    draftKey,
    exerciseLogs,
    expandedInfo,
    loading,
    scheduleId,
    userId,
    workout,
    workoutId,
    workoutNotes,
    workoutStartTime
  ]);

  // --- Save & finish ---
  const saveWorkoutSession = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const elapsedMinutes = Math.round((new Date() - workoutStartTime) / 60000);
      
      // Mark all non-skipped exercises as completed on finish (frictionless)
      const finalLogs = Object.values(exerciseLogs).map(log => {
        if (log.skipped) return log;
        return {
          ...log,
          completed: true,
          sets: log.sets?.map(s => ({ ...s, completed: true })) || []
        };
      });

      const sessionData = {
        workoutId,
        workoutName: workout?.name,
        date: workoutStartTime.toISOString(),
        durationMinutes: elapsedMinutes,
        status: 'completed',
        exerciseLogs: finalLogs,
        exercisesCompleted: finalLogs.filter(l => l.completed).length,
        exercisesSkipped: finalLogs.filter(l => l.skipped).length,
        ...(workoutNotes.trim() && { notes: workoutNotes.trim() }),
        ...(scheduleId && { scheduleId })
      };

      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_URL}/fitness/sessions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(sessionData)
      });
    } catch (err) {
      console.error('Error saving workout:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinishWorkout = async () => {
    await saveWorkoutSession();
    removeActiveWorkoutDraft({ draftKey });
    navigate('/fitness/my-workouts', { 
      state: { 
        workoutCompleted: true, 
        duration: Math.round((new Date() - workoutStartTime) / 60000),
        workoutName: workout?.name 
      } 
    });
  };

  const revertScheduleAfterAbandon = async () => {
    if (!scheduleId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/fitness/my-schedule/${scheduleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ revertFromInProgress: true })
      });
      if (!res.ok) throw new Error('Schedule update failed');
    } catch (e) {
      console.error(e);
      toast.error('Could not update your schedule. Your draft was still cleared.');
    }
  };

  const handleLeaveSaveProgress = () => {
    setShowExitModal(false);
    navigate('/fitness/my-workouts');
  };

  const handleLeaveClearProgress = async () => {
    exitingClearRef.current = true;
    removeActiveWorkoutDraft({ draftKey });
    await revertScheduleAfterAbandon();
    setShowExitModal(false);
    navigate('/fitness/my-workouts');
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    const total = Object.keys(exerciseLogs).length;
    if (total === 0) return 0;
    const done = Object.values(exerciseLogs).filter(
      log => log.completed || log.skipped || log.sets?.some(s => s.completed)
    ).length;
    return Math.round((done / total) * 100);
  };

  const getElapsedTime = () => {
    return Math.round((new Date() - workoutStartTime) / 60000);
  };

  if (loading) {
    return (
      <div className="active-workout">
        <div className="active-workout-loading">
          <div className="loading-spinner" />
          <p>Preparing your workout...</p>
        </div>
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="active-workout">
        <div className="active-workout-error">
          <p>Error: {error || 'Workout not found'}</p>
          <button onClick={() => navigate('/fitness')}>Back to Fitness</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`active-workout${restTimer && restTimeRemaining !== null ? ' active-workout--rest-visible' : ''}`}>
      <div className="active-workout-shell">
        {/* Desktop-only sticky exercise navigator */}
        <aside className="active-workout-rail" aria-label="Exercises">
          <div className="rail-header">
            <span className="rail-title">Exercises</span>
            <span className="rail-progress">{getProgress()}%</span>
          </div>
          <ol className="rail-list">
            {allExercises.map((ex, exIdx) => {
              const railLog = exerciseLogs[exIdx];
              const railDetails = ex.exerciseDetails;
              const completedSets =
                railLog?.sets?.filter((s) => s.completed).length || 0;
              const totalSets = railLog?.sets?.length || ex.sets || 1;
              const status = railLog?.completed
                ? 'done'
                : railLog?.skipped
                  ? 'skipped'
                  : completedSets > 0
                    ? 'in-progress'
                    : 'pending';
              return (
                <li
                  key={exIdx}
                  className={`rail-item rail-item--${status}`}
                >
                  <button
                    type="button"
                    className="rail-item-btn"
                    onClick={() => scrollToExercise(exIdx)}
                    title={railDetails?.name || `Exercise ${exIdx + 1}`}
                  >
                    <span className={`rail-bullet rail-bullet--${ex.blockType || 'main'}`}>
                      {status === 'done' ? (
                        <FaCheck />
                      ) : status === 'skipped' ? (
                        <FaTimes />
                      ) : (
                        <span className="rail-bullet-num">{exIdx + 1}</span>
                      )}
                    </span>
                    <span className="rail-item-body">
                      <span className="rail-item-name">
                        {railDetails?.name || 'Exercise'}
                      </span>
                      <span className="rail-item-meta">
                        {ex.blockType && (
                          <span className={`rail-chip rail-chip--${ex.blockType}`}>
                            {ex.blockType}
                          </span>
                        )}
                        <span className="rail-sets-count">
                          {completedSets}/{totalSets} sets
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <main className="active-workout-main">
          <div className="active-header">
            <button type="button" className="close-btn" onClick={() => setShowExitModal(true)} title="Leave workout">
              <FaTimes />
            </button>
            <div className="header-info">
              <span className="header-workout-name">{workout.name}</span>
              <div className="header-meta">
                <span className="header-timer"><FaClock /> {getElapsedTime()}m</span>
                <span className="header-exercise-count">
                  <FaCheck /> {Object.values(exerciseLogs).filter(l => l.completed || l.skipped || l.sets?.some(s => s.completed)).length}/{allExercises.length}
                </span>
                <span className="header-progress">{getProgress()}%</span>
              </div>
            </div>
            <div className="workout-progress-bar">
              <div className="progress-fill" style={{ width: `${getProgress()}%` }} />
            </div>
          </div>

          <div className="exercise-scroll-list">
        {allExercises.map((ex, exIdx) => {
          const details = ex.exerciseDetails;
          const log = exerciseLogs[exIdx];
          const isSkipped = log?.skipped;
          const isCompleted = log?.completed;
          const isTimeBased = details?.measurementType === 'time' || ex.duration;
          const targetReps = ex.reps || details?.defaultReps;
          const restSeconds = ex.restAfter || details?.restBetweenSets || 60;
          const lastWeight = lastWeights[ex.exerciseId];
          const hasInstructionsExpanded =
            !!expandedInfo[exIdx] && !!details?.instructions;
          const hasGuidanceContent = !!(hasInstructionsExpanded || ex.notes);
          const showTracking = !isSkipped;
          const belowHeaderClass = [
            'exercise-card-below-header',
            showTracking &&
              hasGuidanceContent &&
              'exercise-card-below-header--split',
            showTracking &&
              !hasGuidanceContent &&
              'exercise-card-below-header--tracking-only',
            !showTracking &&
              hasGuidanceContent &&
              'exercise-card-below-header--guidance-only',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div 
              key={exIdx}
              ref={el => exerciseRefs.current[exIdx] = el}
              className={`exercise-card ${isCompleted ? 'completed' : ''} ${isSkipped ? 'skipped' : ''}`}
            >
              {/* Exercise header */}
              <div className="exercise-card-header">
                {/* Clickable thumbnail */}
                <div 
                  className="exercise-card-thumb"
                  onClick={() => details?.video ? setVideoExercise(details) : null}
                  style={{ cursor: details?.video ? 'pointer' : 'default' }}
                >
                  {details?.image ? (
                    <img src={details.image} alt={details?.name} />
                  ) : details?.video ? (
                    <div className="thumb-play-icon"><FaYoutube /></div>
                  ) : (
                    <FaDumbbell className="thumb-placeholder-icon" />
                  )}
                  {details?.video && details?.image && (
                    <div className="thumb-play-overlay"><FaPlay /></div>
                  )}
                </div>
                
                <div className="exercise-card-title">
                  <div className="exercise-card-title-row">
                    <span className={`exercise-block-badge ${ex.blockType}`}>{ex.blockType}</span>
                    <h3>{details?.name || 'Exercise'}</h3>
                  </div>
                  <div className="exercise-card-subtitle">
                    {isTimeBased 
                      ? <span>{formatTime(ex.duration || details?.defaultDuration)}</span>
                      : <span>{log?.sets?.length || 1} sets × {targetReps || '?'} reps</span>
                    }
                    {lastWeight && (
                      <span className="last-weight-badge">Last: {gymKgToDisplay(lastWeight.weight, weightUnit).value} {weightUnit}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="exercise-card-actions-bar">
                <button
                  type="button"
                  className={`card-action-pill ${expandedInfo[exIdx] ? 'active' : ''}`}
                  onClick={() => setExpandedInfo(prev => ({ ...prev, [exIdx]: !prev[exIdx] }))}
                >
                  {expandedInfo[exIdx] ? <FaChevronUp /> : <FaInfoCircle />}
                  <span>{expandedInfo[exIdx] ? 'Hide instructions' : 'How to perform'}</span>
                </button>
                <button
                  type="button"
                  className="card-action-pill"
                  onClick={() => setPickerTarget({ mode: 'swap', exIdx })}
                >
                  <FaExchangeAlt />
                  <span>Swap exercise</span>
                </button>
                <button
                  type="button"
                  className={`card-action-pill skip-toggle ${isSkipped ? 'active' : ''}`}
                  onClick={() => toggleSkipExercise(exIdx)}
                >
                  <FaTimes />
                  <span>{isSkipped ? 'Unskip exercise' : 'Skip exercise'}</span>
                </button>
                <button
                  type="button"
                  className="card-action-pill remove-exercise"
                  onClick={() => handleRemoveExercise(exIdx)}
                >
                  <FaTrash />
                  <span>Remove exercise</span>
                </button>
              </div>

              {(ex.notes || !isSkipped) && (
                <div className={belowHeaderClass}>
                  {hasGuidanceContent && (
                    <div className="exercise-card-guidance-col">
                      {hasInstructionsExpanded && (
                        <div className="exercise-card-instructions">
                          <ol>
                            {details.instructions
                              .sort((a, b) => a.step - b.step)
                              .map((inst, i) => (
                                <li key={i}>{inst.text}</li>
                              ))}
                          </ol>
                        </div>
                      )}
                      {ex.notes && (
                        <div className="exercise-card-notes">
                          <FaInfoCircle /> {ex.notes}
                        </div>
                      )}
                    </div>
                  )}
                  {showTracking && (
                    <div className="exercise-card-tracking-col">
                      <div className="sets-table">
                        <div className="sets-table-header">
                          <span className="st-col st-set">Set</span>
                          {!isTimeBased && (
                            <>
                              <span className="st-col st-weight">Weight ({weightUnit})</span>
                              <span className="st-col st-reps">Reps</span>
                            </>
                          )}
                          {isTimeBased && (
                            <span className="st-col st-duration">Time (s)</span>
                          )}
                          <span className="st-col st-rest">Rest</span>
                          <span className="st-col st-done"></span>
                        </div>

                        {(log?.sets || []).map((set, setIdx) => (
                          <div
                            key={setIdx}
                            className={`sets-table-row ${set.completed ? 'completed' : ''} ${setIdx >= (ex.sets || 1) ? 'added-set' : ''}`}
                          >
                            <span className="st-col st-set">{set.setNumber}</span>

                            {!isTimeBased && (
                              <>
                                <span className="st-col st-weight">
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    value={set.weight ? gymKgToDisplay(set.weight, weightUnit).value : ''}
                                    onChange={(e) =>
                                      updateSetField(
                                        exIdx,
                                        setIdx,
                                        'weight',
                                        e.target.value
                                          ? displayToGymKg(e.target.value, weightUnit)
                                          : null
                                      )
                                    }
                                    placeholder={
                                      lastWeight?.weight
                                        ? String(gymKgToDisplay(lastWeight.weight, weightUnit).value)
                                        : '—'
                                    }
                                    className="set-input"
                                    onFocus={(e) => {
                                      requestAnimationFrame(() => {
                                        e.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                      });
                                    }}
                                  />
                                </span>
                                <span className="st-col st-reps">
                                  <input
                                    type="number"
                                    inputMode="numeric"
                                    value={set.reps || ''}
                                    onChange={(e) =>
                                      updateSetField(
                                        exIdx,
                                        setIdx,
                                        'reps',
                                        e.target.value
                                          ? parseInt(e.target.value)
                                          : null
                                      )
                                    }
                                    placeholder={
                                      targetReps ? String(targetReps) : '—'
                                    }
                                    className="set-input"
                                    onFocus={(e) => {
                                      requestAnimationFrame(() => {
                                        e.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                      });
                                    }}
                                  />
                                </span>
                              </>
                            )}

                            {isTimeBased && (
                              <span className="st-col st-duration">
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  value={set.duration ?? ''}
                                  onChange={(e) =>
                                    updateSetField(
                                      exIdx,
                                      setIdx,
                                      'duration',
                                      e.target.value
                                        ? parseInt(e.target.value)
                                        : null
                                    )
                                  }
                                  placeholder={
                                    ex.duration || details?.defaultDuration
                                      ? String(
                                          ex.duration || details?.defaultDuration
                                        )
                                      : '—'
                                  }
                                  className="set-input"
                                  onFocus={(e) => {
                                    requestAnimationFrame(() => {
                                      e.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                    });
                                  }}
                                />
                                <span className="set-input-unit">s</span>
                              </span>
                            )}

                            <span className="st-col st-rest">
                              <button
                                className="rest-timer-btn"
                                onClick={() =>
                                  startRestTimer(restSeconds, exIdx, setIdx)
                                }
                                title={`Start ${restSeconds}s rest timer`}
                              >
                                <FaStopwatch />
                              </button>
                            </span>

                            <span className="st-col st-done">
                              <button
                                className={`set-done-btn ${set.completed ? 'checked' : ''}`}
                                onClick={() =>
                                  toggleSetComplete(exIdx, setIdx)
                                }
                              >
                                <FaCheck />
                              </button>
                            </span>
                          </div>
                        ))}

                        <div className="add-set-row">
                          <button
                            className="add-set-btn"
                            onClick={() => addSet(exIdx)}
                            title="Add an extra set"
                          >
                            <FaPlus /> Add Set
                          </button>
                          {(log?.sets?.length || 0) > (ex.sets || 1) && (
                            <button
                              className="remove-set-btn"
                              onClick={() => removeSet(exIdx)}
                              title="Remove last added set"
                            >
                              <FaMinus />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isSkipped && (
                <div className="skipped-overlay">
                  <span>Skipped</span>
                  <button onClick={() => toggleSkipExercise(exIdx)}>Undo</button>
                </div>
              )}
            </div>
          );
        })}

            {/* Add Exercise Button */}
            <div className="add-exercise-section">
              <button
                type="button"
                className="add-exercise-inline-btn"
                onClick={() => setPickerTarget({ mode: 'add' })}
              >
                <FaPlus />
                <span>Add Exercise</span>
              </button>
            </div>

            {/* Finish Workout Button */}
            <div className="finish-workout-section">
              <button
                className="finish-workout-btn"
                onClick={() => setShowCompletionModal(true)}
              >
                <FaFlagCheckered />
                <span>Finish Workout</span>
              </button>
            </div>
          </div>
        </main>
      </div>

      {pickerTarget && (
        <ExerciseSearchPicker
          title={pickerTarget.mode === 'swap' ? 'Swap Exercise' : 'Add Exercise'}
          onSelect={handlePickExercise}
          onClose={() => setPickerTarget(null)}
        />
      )}

      {/* Floating Rest Timer */}
      {restTimer && restTimeRemaining !== null && (
        <div className={`floating-rest-timer ${restTimeRemaining === 0 ? 'done' : ''}`}>
          <div className="floating-rest-content">
            <span className="floating-rest-label">
              {restTimeRemaining === 0 ? 'Rest Complete!' : 'Rest'}
            </span>
            <span className="floating-rest-time">{formatTime(restTimeRemaining)}</span>
          </div>
          <div className="floating-rest-actions">
            {restTimeRemaining > 0 && (
              isRestRunning ? (
                <button onClick={pauseRestTimer}><FaPause /></button>
              ) : (
                <button onClick={resumeRestTimer}><FaPlay /></button>
              )
            )}
            <button onClick={dismissRestTimer}><FaTimes /></button>
          </div>
        </div>
      )}

      {/* Leave workout (save or clear draft) */}
      {showExitModal && (
        <div className="completion-modal-overlay" onClick={() => setShowExitModal(false)}>
          <div className="completion-modal exit-workout-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Leave workout?</h2>
            <p className="exit-workout-sub">Your session is saved automatically while you train. Choose what to do with this in-progress session.</p>
            <button type="button" className="finish-btn" onClick={handleLeaveSaveProgress}>
              Save progress &amp; leave
            </button>
            <p className="exit-workout-hint">You can resume later from <strong>My Workouts</strong>.</p>
            <button type="button" className="cancel-finish-btn exit-clear-btn" onClick={handleLeaveClearProgress}>
              Clear progress &amp; leave
            </button>
            <p className="exit-workout-warning">Clears saved reps/sets for this workout{scheduleId ? ' and marks it as not started on your schedule' : ''}.</p>
            <button type="button" className="cancel-finish-btn" onClick={() => setShowExitModal(false)}>
              Cancel — keep training
            </button>
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="completion-modal-overlay">
          <div className="completion-modal">
            <div className="completion-icon">
              <FaFlagCheckered />
            </div>
            <h2>Finish Workout?</h2>
            <p>{workout.name}</p>
            
            <div className="completion-stats">
              <div className="stat">
                <span className="stat-value">{getElapsedTime()}</span>
                <span className="stat-label">minutes</span>
              </div>
              <div className="stat">
                <span className="stat-value">{Object.values(exerciseLogs).filter(l => !l.skipped).length}</span>
                <span className="stat-label">exercises</span>
              </div>
            </div>

            <p className="completion-note-hint">All exercises will be marked complete.</p>

            <div className="workout-notes-section">
              <label htmlFor="workout-notes"><FaStickyNote /> Workout Notes <span className="notes-char-count">{workoutNotes.length}/500</span></label>
              <textarea
                id="workout-notes"
                value={workoutNotes}
                onChange={(e) => setWorkoutNotes(e.target.value.slice(0, 500))}
                placeholder="How did it feel? Any notes for your coach or yourself..."
                rows={3}
                maxLength={500}
              />
            </div>

            <button 
              className="finish-btn" 
              onClick={handleFinishWorkout}
              disabled={isSaving}
            >
              <span>{isSaving ? 'Saving...' : 'Save & Finish'}</span>
            </button>
            <button className="cancel-finish-btn" onClick={() => setShowCompletionModal(false)}>
              Keep Going
            </button>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {videoExercise?.video && (() => {
        const videoUrl = videoExercise.video;
        const match = videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
        const embedUrl = match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1` : null;
        if (!embedUrl) return null;
        return (
          <div className="video-modal-overlay" onClick={() => setVideoExercise(null)}>
            <div className="video-modal" onClick={(e) => e.stopPropagation()}>
              <div className="video-modal-header">
                <h3>{videoExercise.name}</h3>
                <button className="video-modal-close" onClick={() => setVideoExercise(null)}>
                  <FaTimes />
                </button>
              </div>
              <div className="video-modal-body">
                <iframe
                  src={embedUrl}
                  title={videoExercise.name}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ActiveWorkout;
